"use client";

/**
 * WebIndexHeroStats — the four headline figures in the /web-index hero.
 *
 * Visually this matches the inline mini-stats in the /platform hero (left
 * hairline rule, display-text numeral, tracked uppercase label) so the two hub
 * pages read as siblings. The difference is that these are LIVE: /platform
 * counts registry constants, this counts the running system.
 *
 * Aggregate totals only — see node-fleet-live.tsx for why per-node fields from
 * this endpoint must never be rendered.
 */

import { useEffect, useRef, useState } from "react";

type Perf = { pages_indexed?: number };
type Pool = {
  pool?: {
    nodes_online?: number;
    cores?: number;
    bandwidth?: { recv?: number };
  };
};

const PERF_URL = "https://extension.askfinz.ai/api/v1/search/perf";
const POOL_URL = "https://extension.askfinz.ai/api/v1/search/pool/analytics";

/** Count up to `value` whenever it changes; renders via `format`. */
function Ticker({
  value,
  format,
  duration = 1400,
}: {
  value: number | null;
  format: (n: number) => string;
  duration?: number;
}) {
  const [n, setN] = useState(0);
  const from = useRef(0);

  useEffect(() => {
    if (value == null) return;
    const start = from.current;
    const delta = value - start;
    let raf = 0;
    const t0 = performance.now();
    const tick = (now: number) => {
      const p = Math.min((now - t0) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(start + eased * delta);
      if (p < 1) raf = requestAnimationFrame(tick);
      else from.current = value;
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value, duration]);

  if (value == null) return <span className="text-foreground/30">—</span>;
  return <>{format(n)}</>;
}

/* fmtCompact formatted the page total, which is no longer printed while the index is being built. */
const fmtInt = (n: number) => String(Math.round(n));
/* NON-BREAKING SPACE, deliberately. "113.7 TB" is one reading and it broke across two lines in
   the hero, which reads as two stats stacked rather than one number with a unit. */
const fmtTb = (n: number) => `${(n / 1_000_000_000_000).toFixed(1)} TB`;

export function WebIndexHeroStats(
  { initial }: { initial?: { pages?: number | null; nodes?: number | null; cores?: number | null } | null },
) {
  /* Seeded by the server (see live-stats.server.tsx) so the figure is in the HTML on first
     paint rather than replacing a dash after hydration. The client fetch below stays: the page
     is prerendered on a 5-minute timer, and this is what keeps a cached copy current. */
  const [pages, setPages] = useState<number | null>(initial?.pages ?? null);
  const [nodes, setNodes] = useState<number | null>(initial?.nodes ?? null);
  const [cores, setCores] = useState<number | null>(initial?.cores ?? null);
  const [recv, setRecv] = useState<number | null>(null);
  const [live, setLive] = useState(false);

  useEffect(() => {
    let on = true;
    const load = async () => {
      try {
        const [pr, lr] = await Promise.all([
          fetch(PERF_URL, { cache: "no-store" }),
          fetch(POOL_URL, { cache: "no-store" }),
        ]);
        if (pr.ok) {
          const j = (await pr.json()) as Perf;
          if (on && j?.pages_indexed != null) {
            setPages(j.pages_indexed);
            setLive(true);
          }
        }
        if (lr.ok) {
          const j = (await lr.json()) as Pool;
          if (on && j?.pool) {
            setNodes(j.pool.nodes_online ?? null);
            setCores(j.pool.cores ?? null);
          }
        }

        /* BANDWIDTH COMES FROM ITS OWN ENDPOINT, NOT FROM pool.bandwidth.
           The pool figure sums the nodes online at that instant, so it answers "what is the fleet
           moving" and not "how much has been read" — and it FALLS when a machine goes offline.
           Measured 16 September 2026: pool said 6.8 TB while the true total was 113.6 TB, and the
           6.8 was the number on this page. /api/index/bandwidth reads the accumulated counter the
           timeline chart has always drawn, so the hero and the chart now agree by construction. */
        const br = await fetch("/api/index/bandwidth", { cache: "no-store" });
        if (br.ok) {
          const b = (await br.json()) as { recv: number | null };
          if (on) setRecv(b?.recv ?? null);
        }
      } catch {
        /* transient — keep what's on screen */
      }
    };
    load();
    const t = setInterval(load, 20000);
    return () => {
      on = false;
      clearInterval(t);
    };
  }, []);

  // NO INDEX TOTAL. "Pages indexed" was the lead stat here and has been dropped: the index is still
  // being built, and a running total is the one number that invites the only comparison we lose.
  // It also dates badly — every screenshot, cached page and AI summary freezes a figure we have
  // already grown past.
  //
  // What remains describes the MACHINERY doing the reading rather than how much has been read. That
  // holds up at any stage: a reader who sees the size of the fleet and the work it has pulled down
  // infers an operation running at scale without being handed a number to measure against Google.
  // `pages` is still fetched — it drives the live indicator below — it is simply not printed.
  const stats: { value: number | null; format: (n: number) => string; label: string }[] = [
    { value: nodes, format: fmtInt, label: "Machines reading" },
    { value: cores, format: fmtInt, label: "Cores pooled" },
    { value: recv, format: fmtTb, label: "Web read" },
  ];

  return (
    <div>
      <div className="flex items-center gap-2">
        <span
          className={`inline-block h-1.5 w-1.5 rounded-full ${live ? "bg-emerald-500 animate-pulse" : "bg-neutral-400"}`}
          aria-hidden
        />
        <span className="text-[10px] uppercase tracking-[0.2em] text-foreground/55">
          {live ? "Live from the fleet" : "Connecting…"}
        </span>
      </div>
      {/* THREE across, not four. The fourth stat was the index total and was deliberately dropped
          (see above), but the column count stayed — so three tiles sat in a four-wide grid, each
          narrower than it needed to be, and "113.7 TB" wrapped in the last one. */}
      <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-6 max-w-xl">
        {stats.map((s) => (
          <div key={s.label} className="border-l hairline-strong pl-3">
            <div className="display-text is-stat tabular-num whitespace-nowrap text-[clamp(1.6rem,2.6vw,2.2rem)]">
              <Ticker value={s.value} format={s.format} />
            </div>
            <div className="mt-2 text-[10px] uppercase leading-none tracking-[0.2em] text-foreground/65">
              {s.label}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
